import { useEffect, useMemo, useState } from 'react';
import type { Account } from './useAccounts';
import type { AccountStatement, AccountStatementErrorCode } from '../types/AccountStatement';
import { authFetch } from '../lib/authFetch';

const MISSING_CONFIG_PATTERN = /statementcloseday|paymentdueday|configure/i;

const isMissingConfigResponse = (status: number, message?: string): boolean =>
  status === 400 && typeof message === 'string' && MISSING_CONFIG_PATTERN.test(message);

const runWithConcurrency = async <T>(
  items: string[],
  limit: number,
  worker: (item: string) => Promise<T>
): Promise<T[]> => {
  const results: T[] = [];
  let index = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (index < items.length) {
      const currentIndex = index;
      index += 1;
      results[currentIndex] = await worker(items[currentIndex] as string);
    }
  });

  await Promise.all(runners);
  return results;
};

export const useAccountStatements = ({
  accounts,
  token,
  asOfDate,
}: {
  accounts: Account[];
  token: string | null;
  asOfDate: string;
}) => {
  const [statementsByAccountId, setStatementsByAccountId] = useState<Record<string, AccountStatement>>(
    {}
  );
  const [errorsByAccountId, setErrorsByAccountId] = useState<Record<string, AccountStatementErrorCode>>(
    {}
  );
  const [loading, setLoading] = useState(false);

  const creditCardAccountIds = useMemo(
    () =>
      accounts
        .filter((account) => account.accountType === 'creditCard')
        .map((account) => account._id),
    [accounts]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchStatements = async () => {
      if (!token || creditCardAccountIds.length === 0) {
        setStatementsByAccountId({});
        setErrorsByAccountId({});
        setLoading(false);
        return;
      }

      setLoading(true);
      const nextStatements: Record<string, AccountStatement> = {};
      const nextErrors: Record<string, AccountStatementErrorCode> = {};

      await runWithConcurrency(creditCardAccountIds, 5, async (accountId) => {
        try {
          const res = await authFetch(`/api/accounts/${accountId}/statement?asOf=${asOfDate}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            const data = await res.json().catch(() => ({} as { message?: string }));
            nextErrors[accountId] = isMissingConfigResponse(res.status, data.message)
              ? 'missing-config'
              : 'unavailable';
            return;
          }

          const statement = (await res.json()) as AccountStatement;
          nextStatements[accountId] = statement;
        } catch {
          nextErrors[accountId] = 'unavailable';
        }
      });

      if (!cancelled) {
        setStatementsByAccountId(nextStatements);
        setErrorsByAccountId(nextErrors);
        setLoading(false);
      }
    };

    fetchStatements();
    return () => {
      cancelled = true;
    };
  }, [asOfDate, creditCardAccountIds, token]);

  return {
    statementsByAccountId,
    errorsByAccountId,
    loading,
  };
};
